import changeCaseObject from 'change-case-object';

import { getApiCountryByCountryCode } from '/api/sdk.gen';
import type { GetApiCountryByCountryCodeResponse } from '/api/types.gen';
import { toNormaliseErrorInput, type NormaliseErrorInput } from '/shared/functions/normaliseError';

interface GetCountryOptions {
  countryCode?: string | null;
  token?: string | null;
  useB2BMarkets?: boolean;
}

function withContext(error: NormaliseErrorInput, context: string) {
  return error != null && typeof error === 'object' ? { ...error, context } : { error, context };
}

type CountryData = GetApiCountryByCountryCodeResponse;

function normaliseCountry(countryApiData: CountryData) {
  return {
    ...changeCaseObject.camelCase(countryApiData),
    currency: countryApiData.currency,
    vehicleTypes: countryApiData.vehicle_options,
  };
}

type Country = ReturnType<typeof normaliseCountry>;
type ErrorWithContext = ReturnType<typeof withContext>;
type GetCountryStartedAction = {
  type: 'GET_COUNTRY_STARTED';
  timestamp: number;
};
type GetCoreInitiativesStartedAction = {
  type: 'GET_CORE_INITIATIVES_STARTED';
  timestamp: number;
};
type GetCountryFulfilledAction = {
  type: 'GET_COUNTRY_FULFILLED';
  country: Country;
  token: string | null;
};
type GetCoreInitiativesFulfilledAction = {
  type: 'GET_CORE_INITIATIVES_FULFILLED';
  initiatives: CountryData['core_initiatives'];
};
type GetCountryRejectedAction = {
  type: 'GET_COUNTRY_REJECTED';
  error: ErrorWithContext;
};
type GetCoreInitiativesRejectedAction = {
  type: 'GET_CORE_INITIATIVES_REJECTED';
  error: ErrorWithContext;
};
type GetCountryAction =
  | GetCountryStartedAction
  | GetCoreInitiativesStartedAction
  | GetCountryFulfilledAction
  | GetCoreInitiativesFulfilledAction
  | GetCountryRejectedAction
  | GetCoreInitiativesRejectedAction;
type SharedDispatch = (action: GetCountryAction) => GetCountryAction;

const getCountry =
  ({ countryCode = null, token = null, useB2BMarkets = false }: GetCountryOptions) =>
  async (dispatch: SharedDispatch): Promise<void> => {
    try {
      dispatch({ type: 'GET_COUNTRY_STARTED', timestamp: Date.now() });
      dispatch({ type: 'GET_CORE_INITIATIVES_STARTED', timestamp: Date.now() });
      const { data: countryData } = await getApiCountryByCountryCode({
        path: {
          country_code: String(countryCode),
        },
        query: {
          useB2BMarkets,
        },
      });
      if (!countryData) {
        throw new Error('Could not get country');
      }

      // @todo Move to proper normalisation function.
      const country = normaliseCountry(countryData);
      dispatch({ type: 'GET_COUNTRY_FULFILLED', country, token });
      dispatch({ type: 'GET_CORE_INITIATIVES_FULFILLED', initiatives: countryData.core_initiatives });
    } catch (error) {
      const normalisedErrorInput = toNormaliseErrorInput(error);
      dispatch({ type: 'GET_COUNTRY_REJECTED', error: withContext(normalisedErrorInput, 'LOCATION') });
      dispatch({ type: 'GET_CORE_INITIATIVES_REJECTED', error: withContext(normalisedErrorInput, 'INITIATIVES') });
    }
  };

export default getCountry;
